import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Mail, Lock, User, Phone, Building2, ArrowRight, Package, Eye, EyeOff } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { useCustomerAuth } from "../context/CustomerAuthContext";
import { toast } from "sonner";

export default function CustomerAuthPage() {
    const [mode, setMode] = useState("login"); // login, register, guest
    const { login, register, guestLookup } = useCustomerAuth();
    const navigate = useNavigate();

    return (
        <div className="min-h-screen" style={{ backgroundColor: "var(--sg-bg-light)" }}>
            <Navbar />
            <div className="max-w-md mx-auto px-4 py-10">
                {/* Tabs */}
                <div className="flex rounded-lg overflow-hidden border mb-6" style={{ borderColor: "var(--sg-border)" }}>
                    {[
                        { id: "login", label: "Přihlášení" },
                        { id: "register", label: "Registrace" },
                        { id: "guest", label: "Bez účtu" },
                    ].map(t => (
                        <button key={t.id} onClick={() => setMode(t.id)}
                            className={`flex-1 py-2.5 text-sm font-medium transition-colors ${mode === t.id ? "text-white" : "text-gray-500 bg-white hover:bg-gray-50"}`}
                            style={mode === t.id ? { backgroundColor: "var(--sg-primary)" } : {}}
                            data-testid={`auth-tab-${t.id}`}>
                            {t.label}
                        </button>
                    ))}
                </div>

                <div className="bg-white border p-6" style={{ borderColor: "var(--sg-border)", borderRadius: "var(--sg-radius)" }}>
                    {mode === "login" && <LoginForm onLogin={login} navigate={navigate} />}
                    {mode === "register" && <RegisterForm onRegister={register} navigate={navigate} />}
                    {mode === "guest" && <GuestForm onLookup={guestLookup} navigate={navigate} />}
                </div>
            </div>
            <Footer />
        </div>
    );
}

function LoginForm({ onLogin, navigate }) {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPw, setShowPw] = useState(false);
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await onLogin(email, password);
            toast.success("Přihlášení úspěšné");
            navigate("/muj-ucet");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Chyba přihlášení");
        } finally { setLoading(false); }
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            <div className="text-center mb-4">
                <h2 className="text-xl font-bold" style={{ color: "var(--sg-text)" }}>Přihlaste se</h2>
                <p className="text-sm text-gray-500">do svého zákaznického účtu</p>
            </div>
            <div>
                <Label className="text-xs text-gray-600 mb-1">Email</Label>
                <div className="relative">
                    <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <Input value={email} onChange={e => setEmail(e.target.value)} type="email" placeholder="vas@email.cz" className="pl-9 h-10" required data-testid="login-email" />
                </div>
            </div>
            <div>
                <Label className="text-xs text-gray-600 mb-1">Heslo</Label>
                <div className="relative">
                    <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <Input value={password} onChange={e => setPassword(e.target.value)} type={showPw ? "text" : "password"} placeholder="Vaše heslo" className="pl-9 pr-9 h-10" required data-testid="login-password" />
                    <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                        {showPw ? <EyeOff size={14} /> : <Eye size={14} />}
                    </button>
                </div>
            </div>
            <Button type="submit" disabled={loading} className="w-full h-10 text-white" style={{ backgroundColor: "var(--sg-primary)" }} data-testid="login-submit">
                {loading ? "Přihlašuji..." : "Přihlásit se"} <ArrowRight size={14} className="ml-1" />
            </Button>
            <div className="text-center">
                <Link to="/zapomenute-heslo" className="text-sm hover:underline" style={{ color: "var(--sg-primary)" }} data-testid="forgot-password-link">
                    Zapomenuté heslo?
                </Link>
            </div>
        </form>
    );
}

function RegisterForm({ onRegister, navigate }) {
    const [form, setForm] = useState({ email: "", password: "", name: "", phone: "", company: "" });
    const [loading, setLoading] = useState(false);
    const u = (k, v) => setForm(p => ({ ...p, [k]: v }));

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (form.password.length < 6) { toast.error("Heslo musí mít alespoň 6 znaků"); return; }
        setLoading(true);
        try {
            await onRegister(form);
            toast.success("Registrace úspěšná!");
            navigate("/muj-ucet");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Chyba registrace");
        } finally { setLoading(false); }
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-3">
            <div className="text-center mb-3">
                <h2 className="text-xl font-bold" style={{ color: "var(--sg-text)" }}>Vytvořte si účet</h2>
                <p className="text-sm text-gray-500">sledujte objednávky, poptávky a reklamace</p>
            </div>
            <div>
                <Label className="text-xs text-gray-600 mb-1">Jméno a příjmení *</Label>
                <div className="relative"><User size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <Input value={form.name} onChange={e => u("name", e.target.value)} placeholder="Jan Novák" className="pl-9 h-9" required data-testid="reg-name" /></div>
            </div>
            <div>
                <Label className="text-xs text-gray-600 mb-1">Email *</Label>
                <div className="relative"><Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <Input value={form.email} onChange={e => u("email", e.target.value)} type="email" placeholder="vas@email.cz" className="pl-9 h-9" required data-testid="reg-email" /></div>
            </div>
            <div>
                <Label className="text-xs text-gray-600 mb-1">Heslo *</Label>
                <div className="relative"><Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <Input value={form.password} onChange={e => u("password", e.target.value)} type="password" placeholder="Min. 6 znaků" className="pl-9 h-9" required data-testid="reg-password" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs text-gray-600 mb-1">Telefon</Label>
                    <div className="relative"><Phone size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                        <Input value={form.phone} onChange={e => u("phone", e.target.value)} placeholder="+420..." className="pl-9 h-9" /></div></div>
                <div><Label className="text-xs text-gray-600 mb-1">Firma</Label>
                    <div className="relative"><Building2 size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                        <Input value={form.company} onChange={e => u("company", e.target.value)} placeholder="Volitelné" className="pl-9 h-9" /></div></div>
            </div>
            <Button type="submit" disabled={loading} className="w-full h-10 text-white" style={{ backgroundColor: "var(--sg-primary)" }} data-testid="reg-submit">
                {loading ? "Registruji..." : "Zaregistrovat se"}
            </Button>
        </form>
    );
}

function GuestForm({ onLookup, navigate }) {
    const [orderNumber, setOrderNumber] = useState("");
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await onLookup(orderNumber, email);
            toast.success("Objednávka nalezena");
            navigate("/muj-ucet");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Objednávka nenalezena");
        } finally { setLoading(false); }
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            <div className="text-center mb-4">
                <h2 className="text-xl font-bold" style={{ color: "var(--sg-text)" }}>Sledovat objednávku</h2>
                <p className="text-sm text-gray-500">bez nutnosti registrace</p>
            </div>
            <div>
                <Label className="text-xs text-gray-600 mb-1">Číslo objednávky</Label>
                <div className="relative"><Package size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <Input value={orderNumber} onChange={e => setOrderNumber(e.target.value)} placeholder="SG-2026-XXXXX" className="pl-9 h-10 font-mono" required data-testid="guest-order" /></div>
            </div>
            <div>
                <Label className="text-xs text-gray-600 mb-1">Email použitý při objednávce</Label>
                <div className="relative"><Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <Input value={email} onChange={e => setEmail(e.target.value)} type="email" placeholder="vas@email.cz" className="pl-9 h-10" required data-testid="guest-email" /></div>
            </div>
            <Button type="submit" disabled={loading} className="w-full h-10 text-white" style={{ backgroundColor: "var(--sg-primary)" }} data-testid="guest-submit">
                {loading ? "Hledám..." : "Najít objednávku"} <ArrowRight size={14} className="ml-1" />
            </Button>
        </form>
    );
}
